import { useQuery } from '@apollo/client/react';

import { ViewerDocument } from '../../../graphql/__generated__/types';

/**
 * Ce que le portail sait des rattachements de la personne connectée.
 *
 * **Quatre états, et trois d'entre eux se ressemblent à l'œil** : la requête est en
 * vol, la requête a échoué, la personne n'a réellement aucun compte. Les confondre
 * coûte cher dans les deux sens. Une erreur rendue comme une absence enverrait
 * quelqu'un réclamer un accès qu'il détient déjà ; un chargement rendu comme une
 * absence lui dirait la même chose pendant une seconde, puis se dédirait.
 */
export type ViewerAttachment =
    { status: 'loading' } | { status: 'failed'; retry: () => void } | { status: 'attached' } | { status: 'unattached' };

/**
 * **Un seul lecteur du fait « cette personne est-elle rattachée à un compte ? »**,
 * parce que deux écrans le lisent (la page d'accueil et le menu) et que deux
 * lectures d'un même fait divergent.
 *
 * Le cas est devenu atteignable avec PCG-117 : la garde de connexion d'`account`
 * accepte désormais la portée `ACCOUNT`, que toute personne de l'écosystème porte.
 * Une personne authentifiée sans aucun rattachement arrive donc ici.
 *
 * Attention, ce n'est **pas** une garde de sécurité, et il ne faut pas le lire comme
 * tel : le périmètre est une règle serveur, l'API refuse la donnée hors périmètre
 * quoi que le navigateur affiche. Ce qu'on gagne ici est de ne pas laisser une page
 * vide sans un mot, qui se lit comme un portail cassé.
 */
const useViewerAttachment = (): ViewerAttachment => {
    const { data, loading, error, refetch } = useQuery(ViewerDocument);

    /*
     * Attention, on ne refiltre PAS sur `membership.status`. L'API rend déjà les
     * seuls rattachements qui comptent (`active` et `invited`, filtre porté par le
     * résolveur de `viewer`), et c'est le même filtre que `Role.membershipCount` :
     * un second filtre ici ferait dire au portail et au back-office deux nombres
     * différents sur la même personne. Une liste vide est donc la réponse de l'API,
     * pas une déduction du front.
     */
    const memberships = data?.viewer?.memberships ?? [];

    /*
     * L'ordre des tests n'est pas cosmétique. Un rattachement en main est un fait
     * positif qui tranche, même si la requête est repartie ou qu'une erreur
     * partielle accompagne la réponse. Ce n'est qu'ensuite qu'on a le droit de ne
     * pas savoir, et « aucun rattachement » se dit en dernier, quand la réponse est
     * revenue vide ET sans erreur.
     *
     * `viewer` lui-même peut être nul : la personne n'a alors pas de ligne dans notre
     * table `user`, ce qui est le cas le plus fréquent ici, et se lit pareil (aucun
     * compte rattaché).
     */
    if (memberships.length > 0) {
        return { status: 'attached' };
    }
    if (loading) {
        return { status: 'loading' };
    }
    if (error) {
        return {
            status: 'failed',
            retry: () => {
                void refetch();
            },
        };
    }
    return { status: 'unattached' };
};

export default useViewerAttachment;
